/**
 * IssuesWorkList — virtualisierte Produkt-Arbeitsliste (@tanstack/react-virtual).
 * Rendert IssuesProductRow; Empty-State bei keinen Treffern.
 */
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { ProductWithIssues } from '@/lib/api/channel-readiness'
import { IssuesProductRow } from './IssuesProductRow'

interface IssuesWorkListProps {
  products: ProductWithIssues[]
  onFix: (product: ProductWithIssues) => void
}

export function IssuesWorkList({ products, onFix }: IssuesWorkListProps) {
  const { t } = useTranslation()
  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: products.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 132,
    overscan: 6,
  })

  if (products.length === 0) {
    return (
      <p
        data-testid="issues-no-results"
        className="py-12 text-center text-sm text-neutral-500 dark:text-neutral-400"
      >
        {t('productsIssues.workList.noResults')}
      </p>
    )
  }

  return (
    <div ref={parentRef} className="min-h-0 flex-1 overflow-y-auto" data-testid="issues-work-list">
      <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
        {virtualizer.getVirtualItems().map((vi) => {
          const product = products[vi.index]
          return (
            <div
              key={product.product_id}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${vi.start}px)`,
                paddingBottom: 8,
              }}
            >
              <IssuesProductRow product={product} onFix={onFix} />
            </div>
          )
        })}
      </div>
    </div>
  )
}
